import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { UseDataProps } from './types';
import { StepDesc, HistoricalStepData, FieldDesc, AllFieldValueTypes } from '90.quickConnect.Models/models';
import { WorkflowStepStatus } from '90.quickConnect.Models/enums';
import { useStore } from '30.quickConnect.Stores';
import { desactivatedAutomationWkf } from '10.quickConnect.app/components/domain/Declaration/helpers/initForm/desactivatedAutomationWkf';

const useData = (
  historicalStepDataList: HistoricalStepData[],
  currentStepId: string,
  steps: StepDesc[],
  isEditable: boolean,
  flattenFields: FieldDesc[],
  updateDeclaration: (updatedFieldFullPathId: string, newValue: AllFieldValueTypes) => void,
): UseDataProps => {
  const { t } = useTranslation('declaration');
  const {
    DeclarationStore: { setCurrentStepWorkFlow },
  } = useStore();
  // On détermine la step courante: step à remplir
  const currentStepIndex = useMemo(
    () => steps.findIndex((s) => s.id.localeCompare(currentStepId, undefined, { sensitivity: 'base' }) === 0) ?? 0,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentStepId],
  );

  // on récupère le statut des différentes steps
  const stepsTransitionStatus = useMemo(() => {
    const transitionStatusMap = new Map<string, WorkflowStepStatus>();
    steps.forEach((s, index) => {
      if (index < currentStepIndex) transitionStatusMap.set(s.id, WorkflowStepStatus.Validated);
      if (index === currentStepIndex) {
        // En consultation, le currentStep est validé (affiche DoneRoundedIcon)
        // En édition, le currentStep est en cours de remplissage
        transitionStatusMap.set(s.id, isEditable ? WorkflowStepStatus.None : WorkflowStepStatus.Validated);
      }
      if (index > currentStepIndex) transitionStatusMap.set(s.id, WorkflowStepStatus.None);
    });
    return transitionStatusMap;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historicalStepDataList, isEditable]);

  // Onglet courant : onglet sélectionné
  const [valueTabIndex, setValueTabIndex] = useState(currentStepIndex);
  const [selectedStep, setSelectedStep] = useState<StepDesc>(steps[currentStepIndex]);

  // indique les champs de l'onglet courant sont readonly ou non
  const [isReadOnly, setIsReadOnly] = useState(true);

  useEffect(() => {
    if (selectedStep) {
      setCurrentStepWorkFlow(selectedStep);
    }
  }, [selectedStep, setCurrentStepWorkFlow]);
  const stepChanged = useCallback(
    (stepId: string) => {
      const newCurrentStepIndex = steps.findIndex((s) => s.id === stepId);
      if (newCurrentStepIndex <= currentStepIndex) {
        setValueTabIndex(newCurrentStepIndex);
        setSelectedStep(steps[newCurrentStepIndex]);
      } else {
        const messageKey = isEditable
          ? 'formlib_workflow_must_validate_current_step'
          : 'formlib_workflow_step_not_validated_yet';
        toast.warn(t(messageKey).toString());
      }
    },
    [currentStepIndex, steps, t, isEditable],
  );

  useEffect(() => {
    if (isEditable) {
      const checkIsReadOnly = selectedStep
        ? stepsTransitionStatus.get(selectedStep.id) === WorkflowStepStatus.Validated
        : true;
      setIsReadOnly(checkIsReadOnly);
    }
  }, [isEditable, selectedStep, stepsTransitionStatus]);

  // Garder les dernières infos pour éviter des updates inutiles
  const lastRef = useRef<{ fullPathId?: string; value?: any }>({});

  useEffect(() => {
    const next = steps[currentStepIndex];
    if (!next) return;

    setSelectedStep(next);
    setValueTabIndex(currentStepIndex);

    // Eviter de relancer les effets métiers si rien n'a changé
    const sameFullPath = lastRef.current.fullPathId?.toLowerCase() === next.fullPathId?.toLowerCase();
    const sameValue = lastRef.current.value === next.value;
    if (!sameFullPath || !sameValue) {
      lastRef.current = { fullPathId: next.fullPathId, value: next.value };

      desactivatedAutomationWkf(flattenFields, next.id);
      updateDeclaration(next.fullPathId.toLowerCase(), next.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepIndex]);

  return {
    steps,
    selectedStep,
    stepsTransitionStatus,
    stepChanged,
    valueTabIndex,
    isReadOnly,
  };
};

export default useData;
